/**
 * Per-tab persistence for the Copilot conversation.
 *
 * The backend keeps the real conversation in a sandbox keyed by the session id,
 * so surviving a reload only needs the session id plus a rendering snapshot of
 * the messages. sessionStorage is deliberate: it survives reloads but a tab
 * opened normally never sees another tab's conversation, so ordinary multi-tab
 * use cannot stream two chats into one session (the backend fans every delta
 * out to all subscribers of a session). The one gap is "Duplicate tab", which
 * copies sessionStorage: both copies then share the session until one of them
 * starts a new chat. Accepted for now — guarding it needs a cross-tab
 * handshake before hydrating.
 *
 * `pending` is set while a turn is running and cleared when it ends. Finding it
 * on load means the tab was reloaded mid-answer: the backend cancels the turn
 * on disconnect, so the UI offers to send that request again.
 */

const STORAGE_KEY = 'osavul:copilot:conversation';

export type CopilotTurnMode = 'qa' | 'report';

export interface StoredCopilotMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface StoredCopilotConversation {
  sessionId: string;
  userId: number | string;
  workspaceId: number | string;
  narrativeId: number;
  narrativeTitle: string;
  narrativePath: string;
  /** Epoch ms of the last request sent for this session; mirrors the backend's 6 h sliding TTL. */
  lastRequestAt: number;
  messages: StoredCopilotMessage[];
  pending: { text: string; mode: CopilotTurnMode } | null;
}

export function readCopilotConversation(): StoredCopilotConversation | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const stored = JSON.parse(raw) as StoredCopilotConversation;
    if (!stored?.sessionId || !stored?.narrativeId || !Array.isArray(stored.messages)) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return stored;
  } catch {
    return null;
  }
}

export function writeCopilotConversation(conversation: StoredCopilotConversation): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(conversation));
  } catch {
    // Quota or private mode: the chat keeps working, it just won't survive a reload.
  }
}

export function clearCopilotConversation(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to clean up if storage is unavailable.
  }
}
